import dayjs from "dayjs";

import { AUDIT_ACTION_VALUES } from "./audit-log.constants";
import { AuditEvent, AuditPayload } from "../types";

export const EMPTY_VALUE = "-";

type VisualMetadata = {
  label: string;
  color: string;
  className: string;
};

export const getAuditEventId = (event: AuditEvent): string =>
  event.id || event._id || event.request_id || `${event.at || ""}-${event.path || ""}`;

export const getSafeText = (value?: string | number | null): string => {
  if (value === null || value === undefined || value === "") {
    return EMPTY_VALUE;
  }

  return String(value);
};

export const formatAuditDate = (value?: string | null): string => {
  if (!value) {
    return EMPTY_VALUE;
  }

  const parsed = dayjs(value);

  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : EMPTY_VALUE;
};

export const formatAuditTime = (value?: string | null): string => {
  if (!value) {
    return EMPTY_VALUE;
  }

  const parsed = dayjs(value);

  return parsed.isValid() ? parsed.format("HH:mm:ss") : EMPTY_VALUE;
};

export const formatAuditDateTime = (value?: string | null): string => {
  const date = formatAuditDate(value);
  const time = formatAuditTime(value);

  if (date === EMPTY_VALUE && time === EMPTY_VALUE) {
    return EMPTY_VALUE;
  }

  return `${date}, ${time}`;
};

export const getActorInitials = (
  actorName?: string | null,
  actorId?: string | null,
): string => {
  const source = actorName || actorId || "S";
  const words = source
    .trim()
    .split(/\s|[._-]/)
    .filter(Boolean);

  if (!words.length) {
    return "S";
  }

  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }

  return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

export const buildResourceLabelMap = (
  resources: Array<{ label: string; value: string }> = [],
): Record<string, string> =>
  resources.reduce<Record<string, string>>((labels, option) => {
    if (option.value) {
      labels[option.value] = option.label;
    }

    return labels;
  }, {});


export const getResourceLabel = (
  resource?: string | null,
  resourceLabels: Record<string, string> = {},
): string => {
  if (!resource) {
    return EMPTY_VALUE;
  }

  return resourceLabels[resource] || resource;
};

export const getActionMetadata = (action?: string | null): VisualMetadata => {
  const normalized = (action || "").toUpperCase();

  if (normalized.includes(AUDIT_ACTION_VALUES.login)) {
    return { label: "Login", color: "default", className: "audit-log-tag-default" };
  }

  if (
    normalized.includes(AUDIT_ACTION_VALUES.create) ||
    normalized.includes("CRIACAO") ||
    normalized.includes("CRIAÇÃO") ||
    normalized.includes("CRIOU") ||
    normalized === "POST"
  ) {
    return { label: "Criou", color: "green", className: "audit-log-tag-success" };
  }

  if (
    normalized.includes(AUDIT_ACTION_VALUES.delete) ||
    normalized.includes("EXCLUSAO") ||
    normalized.includes("EXCLUSÃO") ||
    normalized.includes("REMOVEU")
  ) {
    return { label: "Removeu", color: "red", className: "audit-log-tag-danger" };
  }

  if (normalized.includes(AUDIT_ACTION_VALUES.cacheFlush) || normalized.includes("CACHE")) {
    return { label: "Limpou cache", color: "blue", className: "audit-log-tag-info" };
  }

  if (
    normalized.includes(AUDIT_ACTION_VALUES.resetPassword) ||
    normalized.includes("RESET")
  ) {
    return { label: "Resetou senha", color: "default", className: "audit-log-tag-default" };
  }

  if (normalized.includes(AUDIT_ACTION_VALUES.changePassword)) {
    return {
      label: "Alterou senha",
      color: "default",
      className: "audit-log-tag-default",
    };
  }

  if (
    normalized.includes(AUDIT_ACTION_VALUES.patch) ||
    normalized.includes("ALTERACAO") ||
    normalized.includes("ALTERAÇÃO") ||
    normalized.includes("ALTER")
  ) {
    return { label: "Alterou", color: "blue", className: "audit-log-tag-info" };
  }

  if (
    normalized.includes(AUDIT_ACTION_VALUES.update) ||
    normalized.includes("PUT") ||
    normalized.includes("ATUALIZACAO") ||
    normalized.includes("ATUALIZAÇÃO") ||
    normalized.includes("ATUAL")
  ) {
    return { label: "Atualizou", color: "blue", className: "audit-log-tag-info" };
  }

  return {
    label: action || EMPTY_VALUE,
    color: "default",
    className: "audit-log-tag-default",
  };
};

export const getMethodMetadata = (method?: string | null): VisualMetadata => {
  const normalized = (method || "").toUpperCase();

  switch (normalized) {
    case "POST":
      return { label: "POST", color: "green", className: "audit-log-method-post" };
    case "PUT":
      return { label: "PUT", color: "blue", className: "audit-log-method-put" };
    case "PATCH":
      return { label: "PATCH", color: "orange", className: "audit-log-method-patch" };
    case "DELETE":
      return { label: "DELETE", color: "red", className: "audit-log-method-delete" };
    case "LOGIN":
      return { label: "LOGIN", color: "default", className: "audit-log-method-default" };
    default:
      return {
        label: normalized || EMPTY_VALUE,
        color: "default",
        className: "audit-log-method-default",
      };
  }
};

export const getOutcomeMetadata = (
  outcome?: string | null,
): VisualMetadata => {
  const normalized = (outcome || "").toLowerCase();

  if (normalized === "success") {
    return { label: "Sucesso", color: "green", className: "audit-log-result-success" };
  }

  if (normalized === "failure" || normalized === "error") {
    return { label: "Falha", color: "red", className: "audit-log-result-failure" };
  }

  return {
    label: outcome || EMPTY_VALUE,
    color: "default",
    className: "audit-log-result-default",
  };
};

export const formatAuditPayload = (payload: AuditPayload): string => {
  if (payload === undefined) {
    return EMPTY_VALUE;
  }

  if (typeof payload === "string") {
    return payload || EMPTY_VALUE;
  }

  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return String(payload);
  }
};
